import React from 'react';
import LoadingSpinner from './LoadingSpinner';
import useIsTestVersion from '../hooks/useIsTestVersion';

/**
 * The portal's single button. It is design-aware:
 *
 * - Control (old design) renders exactly the historical styles — unchanged, so
 *   the A/B control stays byte-for-byte identical.
 * - New look renders the standardized 4-intent system:
 *     primary   → orange filled  (the one CTA on a screen; orange is reserved for it)
 *     secondary → brown  filled  (a solid supporting action)
 *     outline   → brown  border  (low-commitment / reversible: View, Reschedule, Cancel)
 *     ghost     → brown  text    (quiet, dismissible: Start over, Apply discount)
 *   Modifiers (any variant): size (xs/sm/md/lg), pill, dashed, fullWidth, loading,
 *   and tone="danger" for destructive actions.
 *
 * Legacy variant names still work and alias onto the four in the new look, so
 * existing call-sites keep rendering without edits.
 */

// ---- Control (old design) — preserved verbatim. Do not restyle. ----
const CONTROL_BASE = 'font-bold rounded-md transition-colors focus:outline-none inline-flex items-center justify-center';
const CONTROL_SIZE = {
  xs: 'p-1.5 text-[10px] xxs:p-1.5 xxs:text-[11px] xs:p-2 xs:text-xs md:p-1.5',
  sm: 'p-2 text-[11px] xxs:p-2.5 xxs:text-xs xs:p-3 xs:text-xs md:p-2',
  md: 'p-2.5 text-xs xxs:p-3 xxs:text-[13px] xs:p-4 xs:text-sm md:p-2',
  lg: 'p-3 text-sm xxs:p-4 xxs:text-sm xs:p-5 xs:text-base md:p-3',
};
const CONTROL_VARIANT = {
  primary: 'bg-primary text-white hover:bg-primary-dark',
  secondary: 'bg-light_red text-primary hover:bg-[#ffe8e5]',
  outline: 'border border-2 border-primary text-primary hover:bg-[#fff8f7]',
  text: 'text-primary hover:bg-[#fff8f7]',
  ghost: 'text-primary hover:bg-[#fff8f7]',
  quaternary: 'text-primary hover:bg-[#fff8f7]',
  danger: 'bg-red-500 text-white hover:bg-red-600',
  cancel: 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100',
  brownOutline: 'bg-white border border-2 border-[#544947] text-[#544947] hover:bg-[#544947]/5',
  shallow: 'bg-light_red text-primary hover:bg-[#fff7f0] border-0 rounded-full px-2 py-2 text-sm w-fit',
};

// ---- New look — the standardized 4-intent system. ----
// Radius lives outside NEW_BASE: `pill` swaps rounded-lg for rounded-full, and
// hardcoding rounded-lg here let it win the stylesheet order over rounded-full.
const NEW_BASE = 'font-semibold transition-colors focus:outline-none inline-flex items-center justify-center gap-2 disabled:opacity-50';
const NEW_SIZE = {
  xs: 'px-3 py-1.5 text-xs',
  sm: 'px-4 py-2 text-[13px]',
  md: 'px-6 py-3 text-sm',
  lg: 'px-7 py-3.5 text-base',
};
// The quaternary tier reads as a text link, so it drops the heavy horizontal padding.
const NEW_QUIET_SIZE = {
  xs: 'px-2 py-1.5 text-xs',
  sm: 'px-2.5 py-2 text-[13px]',
  md: 'px-3 py-2.5 text-sm',
  lg: 'px-3 py-3 text-base',
};
// Hierarchy: primary (solid orange, the one CTA) → secondary (warm peach + orange)
// → tertiary (soft brown fill) → quaternary (brown text link). Each tier below
// primary is a soft/quiet treatment, so the solid-orange primary always stands alone.
// Hierarchy: primary (solid orange, the one CTA) → secondary (glassy card "Add"
// look) → tertiary (ghost text link, dashed hover) → outline (brown border).
const NEW_VARIANT = {
  primary: 'bg-primary text-white hover:bg-primary/90',
  secondary: 'bg-white/70 backdrop-blur-md border border-white/60 text-gray-700 shadow-[inset_0_1px_2px_rgba(255,255,255,0.9),0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[inset_0_2px_4px_rgba(255,255,255,1),0_4px_12px_rgba(0,0,0,0.08)]',
  tertiary: 'bg-transparent text-[#544947] hover:bg-[#544947]/10 hover:underline hover:decoration-dashed underline-offset-2 decoration-[#544947]/60',
  outline: 'bg-transparent border-2 border-[#544947] text-[#544947] hover:bg-[#544947]/5',
  danger: 'bg-[#d64545] text-white hover:bg-[#bd3a3a]',
};
// Legacy names → the canonical intents.
const NEW_ALIAS = {
  text: 'tertiary',
  ghost: 'tertiary',
  quaternary: 'tertiary',
  shallow: 'tertiary',
  cancel: 'outline',
  brownOutline: 'outline',
};

const Button = React.forwardRef(({
  children,
  onClick,
  variant, // primary | secondary | tertiary | outline | quaternary (+ legacy: text, ghost, cancel, brownOutline, shallow, danger)
  size = 'md',         // xs | sm | md | lg
  fullWidth = false,
  pill = false,        // new look: rounded-full (chips/toggles)
  dashed = false,      // new look: dashed "add" affordance
  tone,                // 'danger' for destructive actions (new look)
  className = '',
  disabled = false,
  loading = false,
  type = 'button',
  as = 'button',       // 'button' | 'a'
  href,
  target,
  rel,
  ...props
}, ref) => {
  const isTest = useIsTestVersion();
  // Anchor "buttons" default to the quiet ghost/quaternary tier; real buttons to primary.
  const effectiveVariant = variant || (as === 'a' ? 'ghost' : 'primary');

  let buttonClasses;
  if (isTest) {
    // Resolve to one of the canonical intents (danger tone wins).
    const resolved = tone === 'danger'
      ? 'danger'
      : (NEW_VARIANT[effectiveVariant] ? effectiveVariant : (NEW_ALIAS[effectiveVariant] || 'primary'));
    const isQuiet = resolved === 'tertiary';
    const sizeClass = (isQuiet ? NEW_QUIET_SIZE : NEW_SIZE)[size] || NEW_SIZE.md;
    const variantClass = dashed
      ? 'bg-transparent border-2 border-dashed border-gray-200 text-gray-600 hover:border-primary hover:text-primary'
      : NEW_VARIANT[resolved];
    buttonClasses = `
      ${NEW_BASE}
      ${pill ? 'rounded-full' : 'rounded-lg'}
      ${sizeClass}
      ${variantClass}
      ${fullWidth ? 'w-full' : ''}
      ${(disabled || loading) ? 'cursor-not-allowed' : 'cursor-pointer'}
      ${className}
    `;
  } else {
    // Control — historical rendering, untouched.
    const isText = effectiveVariant === 'text' || effectiveVariant === 'ghost' || effectiveVariant === 'quaternary';
    buttonClasses = `
      ${CONTROL_BASE}
      ${isText ? '' : (CONTROL_SIZE[size] || CONTROL_SIZE.md)}
      ${CONTROL_VARIANT[effectiveVariant] || CONTROL_VARIANT.primary}
      ${fullWidth ? 'w-full' : ''}
      ${(disabled || loading) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      ${className}
    `;
  }

  // Spinner ink: white only on the solid filled variants (primary / danger);
  // the soft-fill / quiet tiers carry colored text, so their spinner inherits it.
  const spinnerOnLight = isTest
    ? (tone === 'danger' || effectiveVariant === 'primary' || effectiveVariant === 'danger')
    : (effectiveVariant === 'primary' || effectiveVariant === 'danger');

  if (as === 'a') {
    return (
      <a
        ref={ref}
        href={href}
        target={target}
        rel={rel}
        className={buttonClasses}
        onClick={onClick}
        {...props}
      >
        {children}
      </a>
    );
  }

  return (
    <button
      ref={ref}
      type={type}
      className={buttonClasses}
      onClick={onClick}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <LoadingSpinner
          size={size}
          className={spinnerOnLight ? 'text-white' : ''}
        />
      ) : (
        children
      )}
    </button>
  );
});

export default Button;
